import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const UploadContract = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    try {
      await api.uploadContract(file);
      toast.success('Contrato subido exitosamente');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el contrato');
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F0' }}>
      <nav className="bg-white border-b border-border shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="contractcheck360" className="h-10 w-auto" />
              <span className="text-xl brand-name" style={{ color: '#1A4D2E' }}>
                contractcheck360
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" data-testid="back-to-dashboard-btn">
                Volver al Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 max-w-3xl py-12">
        <h2 
          className="text-3xl md:text-4xl font-bold mb-4"
          style={{ fontFamily: 'Merriweather, serif', color: '#1A4D2E' }}
        >
          Subir Contrato
        </h2>
        <p className="text-lg mb-8" style={{ color: '#64748B' }}>
          Sube tu contrato en formato PDF, Word o imagen para comenzar el análisis
        </p>

        <div className="bg-white border border-border shadow-sm rounded-sm p-8">
          <form onSubmit={handleSubmit}>
            <div
              className={`border-2 border-dashed rounded-sm p-12 text-center ${
                dragActive ? 'border-primary bg-primary/5' : 'border-border'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="drop-zone"
            >
              <Upload className="w-16 h-16 mx-auto mb-4" style={{ color: '#1A4D2E' }} />
              {file ? (
                <div>
                  <FileText className="w-12 h-12 mx-auto mb-2" style={{ color: '#1A4D2E' }} />
                  <p className="font-medium mb-2">{file.name}</p>
                  <p className="text-sm mb-4" style={{ color: '#64748B' }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setFile(null)}
                    data-testid="remove-file-btn"
                  >
                    Cambiar Archivo
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="text-lg font-medium mb-2">
                    Arrastra tu archivo aquí o haz clic para seleccionar
                  </p>
                  <p className="text-sm mb-4" style={{ color: '#64748B' }}>
                    Formatos soportados: PDF, DOC, DOCX, JPG, PNG (Máx. 10MB)
                  </p>
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={handleChange}
                    data-testid="file-input"
                  />
                  <label htmlFor="fileInput">
                    <Button
                      type="button"
                      onClick={() => document.getElementById('fileInput').click()}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      style={{ backgroundColor: '#1A4D2E' }}
                      data-testid="select-file-btn"
                    >
                      Seleccionar Archivo
                    </Button>
                  </label>
                </div>
              )}
            </div>

            <div 
              className="mt-6 p-4 rounded-sm flex items-start gap-3"
              style={{ backgroundColor: '#FFF7ED', border: '1px solid #FDBA74' }}
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F97316' }} />
              <div>
                <p className="text-sm font-medium mb-1" style={{ color: '#0F172A' }}>
                  Nota Importante
                </p>
                <p className="text-sm" style={{ color: '#64748B' }}>
                  Después de subir tu contrato, deberás proceder al pago para realizar el análisis.
                  El costo es de $59.90 MXN por análisis individual.
                </p>
              </div>
            </div>

            <Button
              type="submit"
              disabled={!file || uploading}
              className="w-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90 h-12"
              style={{ backgroundColor: '#1A4D2E' }}
              data-testid="upload-submit-btn"
            >
              {uploading ? 'Subiendo...' : 'Subir Contrato'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadContract;
